import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { BehaviorSubject, Observable, throwError, of, Subscription } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import { ResultData } from '@app/core/_common/model';
import { environment } from '@app/env-service';

import { Client, Billing } from '../store/client.model';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  url :string = environment.url;
  urlV2 :string = environment.urlV2;

  private readonly _record = new BehaviorSubject<ResultData>(<ResultData>{});
  public record$ = this._record.asObservable();

  private readonly _activeRecord = new BehaviorSubject<Client>(<Client>{});
  public activeRecord$ = this._activeRecord.asObservable();

  public gettingRecords :boolean = false;

  private readonly _clientPurchaseOrders = new BehaviorSubject<Client>(<Client>{});
  public clientPurchaseOrders$ = this._clientPurchaseOrders.asObservable();
  public gettingClientPurchaseOrders :boolean = false;

  private readonly _clientPurchaseOrderItems = new BehaviorSubject<Client>(
    <Client>{},
  );
  public clientPurchaseOrderItems$ = this._clientPurchaseOrderItems.asObservable();
  public gettingClientPurchaseOrderItems :boolean = false;

  private readonly _billingInfoRecord = new BehaviorSubject<Client>(<Client>{});
  public billingInfoRecord$ = this._billingInfoRecord.asObservable();

  private readonly _invoiceStats = new BehaviorSubject<any>(<any>[]);
  public invoiceStats$ = this._invoiceStats.asObservable();

  private params :any = {
    per_page: 20,
    page: 1,
  };

  excelFileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  excelFileExtension = '.xlsx';

  constructor(
    private http :HttpClient,
    private toastr :ToastrService,
  ) {}

  getRfqQuotations(id :number, params :any) :Observable<any> {
    return this.http
      .get(`${this.url}/clients/${id}/quotation-requests`, { params: params })
      .pipe(
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  getPurchaseOrders(id :number, params :any) {
    this.gettingClientPurchaseOrders = true;

    return this.http
      .get(`${this.url}/clients/${id}/po`, { params: params })
      .subscribe((res :any) => {
        this.gettingClientPurchaseOrders = false;
        this._clientPurchaseOrders.next(res);
      });
  }

  getPurchaseOrderItems(id :number, params :any) {
    this.gettingClientPurchaseOrderItems = true;

    return this.http
      .get(`${this.url}/clients/${id}/po-items`, { params: params })
      .subscribe((res :any) => {
        this.gettingClientPurchaseOrderItems = false;
        this._clientPurchaseOrderItems.next(res);
      });
  }

  getPendingOrders(id :number, params :any) :Observable<any> {
    return this.http
      .get(`${this.url}/clients/${id}/pending-orders`, { params: params })
      .pipe(
        catchError((error) => {
          return throwError(error);
        }),
      );
  }

  refreshList() {
    this.get(this.params);
  }

  setParams(params :any) {
    this.params = params;
  }

  setActive(client :Client) {
    this.gettingRecords = false;

    this._activeRecord.next(client);
  }

  getActive() {
    return this._activeRecord.getValue();
  }

  setActiveById(id :number) {
    this.gettingRecords = true;

    this.getById(id).subscribe((client) => {
      this.setActive(client);
    });
  }

  getById(id :number) :Observable<any> {
    return this.http.get<any>(`${this.url}/clients/${id}`).pipe(
      catchError((error) => {
        return throwError(error);
      }),
    );
  }

  getAsync(params :any) {
    this.gettingRecords = true;

    return this.http.get(`${this.url}/clients`, { params: params }).pipe(
      tap((resp :ResultData) => {
        this.gettingRecords = false;
      }),
    );
  }

  get(params :any) {
    this.gettingRecords = true;

    return this.http
      .get(`${this.url}/clients`, { params: params })
      .subscribe((resp :ResultData) => {
        this._record.next(resp);
        this.gettingRecords = false;
      });
  }

  getHubspot(params :any) {
    return this.http.get(`${this.url}/hubspot_clients`, { params: params });
  }

  removeHubspotAccount(account_id :number) {
    return this.http.delete(`${this.url}/hubspot_clients/${account_id}`);
  }

  assignClientAccount(account_id :number) {
    return this.http.post(`${this.url}/hubspot_clients/${account_id}`, {
      account_id: account_id,
    });
  }

  public search = (
    keyword :string,
    includeRelations ?:boolean,
  ) :Observable<any> => {
    const params :any = { business_name: keyword };
    if (includeRelations) {
      params.relations = 1;
    }

    return this.http
      .get<any>(`${this.url}/clients`, { params: params })
      .pipe(map((resp) => resp.data));
  };

  // public search = (keyword: string): Observable<any> => {
  //     const params = { business_name: keyword };
  //     return this.http.get(`${this.url}/clients`, { params: params }).pipe(
  //         map((resp: any) => resp.data.map((item: any) => item))
  //         // map((resp: any) => resp.data.map((item: any) => {
  //         //     return { display: item.business_name, value: item.id}
  //         // }))
  //     );
  // }

  getNext() {
    this.params.page = this.params.page + 1;
    this.refreshList();
  }

  getPrevious() {
    this.params.page = this.params.page - 1;
    this.refreshList();
  }

  public getDefaultClientBilledTo(
    default_billable_user_id :number,
  ) :Observable<any> {
    return this.http.get(
      `${this.url}/clients/${default_billable_user_id}/defaultClientBilledTo`,
      {},
    );
  }

  public getClientPaymentProfile(client_id :number) :Observable<any> {
    return this.http.get(
      `${this.url}/clients/${client_id}/clientPaymentProfile`,
      {},
    );
  }

  public billClientUser(client_id :number, params :any) :Observable<any> {
    return this.http.post(`${this.url}/clients/${client_id}/billClientUser`, {
      params: params,
    });
  }

  public notifyBillableClientUser(client_id :number, params :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/${client_id}/notifyBillableClientUser`,
      {
        params: params,
      },
    );
  }

  deleteClient(client_id :number) {
    return this.http.delete(`${this.url}/clients/${client_id}`);
  }

  deactivateClient(client_id :number) {
    return this.http.put(`${this.url}/clients/${client_id}`, {});
  }

  activateClient(client_id :number) {
    return this.http.put(`${this.url}/clients/${client_id}`, { activate: 1 });
  }

  saveProfile(client_id :number, data :any) {
    return this.http.post(`${this.url}/clients/${client_id}`, data).pipe(
      tap((data :any) => {
        this._activeRecord.next(data.record);
      }),
    );
  }

  createProfile(data :any) {
    return this.http.post(`${this.url}/clients`, data).pipe(
      tap((resp :any) => {
        const record = this._record.getValue();
        if (record && record.data) {
          record.data.splice(0, 0, resp.record);
          this._record.next(record);
        }
      }),
    );
  }

  saveGoTeamMembers(client_id :number, data :any) {
    return this.http.post(`${this.url}/clients/${client_id}/goteam`, data).pipe(
      tap((data :any) => {
        this._activeRecord.next(data.record);
      }),
    );
  }

  saveBulkGoTeamMembers(data :any) {
    return this.http.post(`${this.url}/clients/goteam`, data);
  }

  updateBilling(client_id :number, billing :Billing) :Observable<any> {
    return this.http.post(`${this.url}/clients/${client_id}/billing`, billing).pipe(
      tap((data :any) => {
        if (data.record) {
          this._activeRecord.next(data.record);
        }
      }),
    );
  }

  setClientSubscription(payload :any) :Observable<any> {
    return this.http.post(`${this.url}/clients/subscriptions`, payload);
  }

  uploadClient(fd :FormData) {
    return this.http.post(`${this.url}/clients/upload`, fd);
  }

  updatePricingStructure(id :number, payload :any) {
    return this.http
      .post(`${this.url}/clients/${id}/update-structure`, payload)
      .pipe(
        tap((resp) => {
          let activeClient = this._activeRecord.getValue();
          if (activeClient && activeClient.id == id) {
            activeClient = Object.assign(activeClient, payload);
            this._activeRecord.next(activeClient);
          }
        }),
      );
  }

  updateClientTemplate(payload :any, id :number) {
    return this.http
      .post(`${this.url}/clients/${id}/update-client-product-template`, payload)
      .pipe(
        tap((resp :any) => {
          const activeClient = this._activeRecord.getValue();
          if (activeClient && activeClient.id == id) {
            this._activeRecord.next(resp);
          }
        }),
      );
  }

  saveDiscountStructure(
    client_id :number,
    discountStructure :any,
  ) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/${client_id}/discount_structure`,
      discountStructure,
    );
  }

  saveMarkupStructure(client_id :number, markupStructure :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/${client_id}/markup_structure`,
      markupStructure,
    );
  }

  saveNewDiscountStructure(
    client_id :number,
    discountStructure :any,
  ) :Observable<any> {
    return this.http
      .post(
        `${this.url}/clients/${client_id}/discount_structure_amount`,
        discountStructure,
      )
      .pipe(
        tap((resp) => {
          this._activeRecord.next(resp);
        }),
      );
  }

  saveNewMarkupStructure(client_id :number, markupStructure :any) :Observable<any> {
    return this.http
      .post(
        `${this.url}/clients/${client_id}/markup_structure_amount`,
        markupStructure,
      )
      .pipe(
        tap((resp) => {
          this._activeRecord.next(resp);
        }),
      );
  }

  downloadTemplate() :void {
    const fileName :string = 'purchase-order-upload-template';

    // Records sheet
    const jsonData :any = [
      { SKU: 'SKU1', QTY: 5, UNIT_PRICE: 1500, IS_KIT: 0 },
      { SKU: 'SKU2', QTY: 6, UNIT_PRICE: 11, IS_KIT: 0 },
      { SKU: 'SKU3', QTY: 7, UNIT_PRICE: '', IS_KIT: 0 },
      { SKU: 'KIT1', QTY: 8, UNIT_PRICE: 50, IS_KIT: 1 },
    ];

    const recordsSheet :XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);

    ['A1', 'B1'].map((k) => {
      recordsSheet[k].s = { font: { bold: true } };
    });

    recordsSheet['!cols'] = [{ wch: 50 }, { wch: 50 }, { wch: 50 }, { wch: 50 }];

    const wb :XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, recordsSheet, 'Records');

    const excelBuffer :any = XLSX.write(wb, {
      bookType: 'csv',
      type: 'array',
    });

    this.saveExcelFile(excelBuffer, fileName, '.csv');
  }

  private saveExcelFile(buffer :any, fileName :string, ext ?:string) :void {
    const data :Blob = new Blob([buffer], { type: this.excelFileType });
    FileSaver.saveAs(data, fileName + (ext ? ext : this.excelFileExtension));
  }

  delete(ids :any) {
    return this.http.post(`${this.url}/clients/remove`, { ids }).pipe(
      tap(
        (resp :any) => {
          if (resp?.clients) {
            resp?.clients.forEach((client) => {
              ids = ids.filter((clientId) => {
                return clientId !== client.id;
              });
            });
          }

          this.toastr.success(resp?.message);

          const record = this._record.getValue();
          if (record && Array.isArray(record.data)) {
            const newRecord :any = record.data.filter((e) => !ids.includes(e.id));
            this._record.next({
              ...record,
              data: newRecord,
            });
          }
        },
        (error) => {
          this.toastr.error(error?.error?.message);
        },
      ),
    );
  }

  saveBillingInfo(payload :any, clientId :number) {
    return this.http
      .post(`${this.url}/clients/${clientId}/billing-info`, payload)
      .pipe(
        tap((resp :any) => {
          this._billingInfoRecord.next(resp);
        }),
      );
  }

  getBillingInfo(clientId :number) {
    return this.http.get(`${this.url}/clients/${clientId}/billing-info`).pipe(
      tap((resp :any) => {
        this._billingInfoRecord.next(resp);
      }),
    );
  }

  getTemplate(params :any) {
    return this.http.get(`${this.url}/client-pricing-structure-setting`, params);
  }

  saveNote(clientId :number, payload :any) :Observable<any> {
    return this.http.post(`${this.url}/clients/${clientId}/save-comment`, payload);
  }

  deleteNoteAttachment(clientId :number, payload :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/${clientId}/delete-comment-attachment`,
      payload,
    );
  }

  deleteNote(clientId :number, payload :any) :Observable<any> {
    return this.http.post(
      `${this.url}/clients/${clientId}/delete-comment`,
      payload,
    );
  }

  getNoteTabs(clientId :number) {
    return this.http.get(`${this.url}/clients/note-groups/${clientId}`);
  }

  addNoteTab(payload :any) {
    return this.http.post(`${this.url}/clients/note-groups`, payload);
  }

  updateNoteTab(tabId :number, payload :any) {
    return this.http.put(`${this.url}/clients/note-groups/${tabId}`, payload);
  }

  deleteNoteTab(tabId :number) {
    return this.http.delete(`${this.url}/clients/note-groups/${tabId}`);
  }

  updateActiveClientPrimaryContacts(newContactList :any[]) :void {
    const activeClient :any = this._activeRecord.getValue();
    if (!activeClient) {
      return;
    }
    activeClient.primary_contacts = newContactList;
    this._activeRecord.next(activeClient);
  }

  clearActiveClient() {
    this._activeRecord.next(null);
  }

  getInvoiceBalance(params :any) :Subscription {
    this.gettingClientPurchaseOrders = true;

    return this.http
      .get(`${this.url}/clients/purchase_orders/balance`, { params: params })
      .subscribe((res :any) => {
        this.gettingClientPurchaseOrders = false;
        this._invoiceStats.next(res);
      });
  }

  getClientStates() {
    return this.http.get(`${this.url}/client-states`);
  }

  exportCompany(
    params :{ [param :string] :string | string[] },
    filename :string,
  ) :Subscription {
    return this.http
      .get(`${this.urlV2}/business-accounts/export`, {
        params,
        responseType: 'blob',
      })
      .subscribe(
        (resp :any) => {
          this.gettingRecords = false;
          FileSaver.saveAs(resp, filename);
          Swal.close();
        },
        (error) => {
          Swal.close();
          Swal.fire('Error Export');
        },
      );
  }

  exportContact(
    params :{ [param :string] :string | string[] },
    filename :string,
  ) :Subscription {
    return this.http
      .get(`${this.urlV2}/user-accounts/export`, {
        params,
        responseType: 'blob',
      })
      .subscribe(
        (resp :any) => {
          this.gettingRecords = false;
          FileSaver.saveAs(resp, filename);
          Swal.close();
        },
        (error) => {
          console.error(error);
          Swal.close();
          Swal.fire('Error Export');
        },
      );
  }
}
